import { fetchJson, mutate, qs } from './base-roros.service';

// ─── Governance ─────────────────────────────────────────────────────────────

export interface RorosConstitutionEntryDTO {
  id: string;
  orgId: string;
  title: string;
  description: string;
  category: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface RorosDecisionRightsDTO {
  orgId: string;
  rights: Record<string, unknown>;
  updatedAt: string;
}

export interface RorosExplorationZoneDTO {
  id: string;
  orgId: string;
  domainId: string;
  title: string;
  description: string;
  scope: Record<string, unknown>;
  excludedCategories: unknown[];
  expiresAt: string;
  decisionNoteId: string | null;
  status: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RorosSourceOfTruthDTO {
  orgId: string;
  entries: Record<string, unknown>[];
}

export const RorosGovernanceService = {
  getConstitution: () =>
    fetchJson<RorosConstitutionEntryDTO[]>('/api/v1/governance/constitution'),
  addConstitutionEntry: (payload: Record<string, unknown>) =>
    mutate<RorosConstitutionEntryDTO>('/api/v1/governance/constitution', 'POST', payload),
  updateConstitutionEntry: (id: string, payload: Record<string, unknown>) =>
    mutate<RorosConstitutionEntryDTO>(`/api/v1/governance/constitution/${id}`, 'PUT', payload),
  deleteConstitutionEntry: (id: string) =>
    mutate(`/api/v1/governance/constitution/${id}`, 'DELETE'),
  getDecisionRights: () =>
    fetchJson<RorosDecisionRightsDTO>('/api/v1/governance/decision-rights'),
  updateDecisionRights: (payload: Record<string, unknown>) =>
    mutate<RorosDecisionRightsDTO>('/api/v1/governance/decision-rights', 'PUT', payload),
  listExplorationZones: (params?: Record<string, string>) =>
    fetchJson<RorosExplorationZoneDTO[]>(`/api/v1/governance/exploration-zones${qs(params)}`),
  createExplorationZone: (payload: Record<string, unknown>) =>
    mutate<RorosExplorationZoneDTO>('/api/v1/governance/exploration-zones', 'POST', payload),
  getExplorationZone: (id: string) =>
    fetchJson<RorosExplorationZoneDTO>(`/api/v1/governance/exploration-zones/${id}`),
  revokeExplorationZone: (id: string) =>
    mutate<RorosExplorationZoneDTO>(`/api/v1/governance/exploration-zones/${id}/revoke`, 'POST'),
  closeExplorationZone: (id: string, payload: { decision_note_id: string }) =>
    mutate<RorosExplorationZoneDTO>(`/api/v1/governance/exploration-zones/${id}/close`, 'POST', payload),
  getSourceOfTruth: () =>
    fetchJson<RorosSourceOfTruthDTO>('/api/v1/governance/source-of-truth'),
};

// ─── Role Contracts ─────────────────────────────────────────────────────────

/**
 * Full Role Contract DTO matching the backend `toRoleContractDTO` output.
 *
 * All 8 GOV-06 components are surfaced as discriminated jsonb shapes so the
 * J7-W2 full editor can read/write each one. Legacy fields (`title`,
 * `description`, etc.) remain optional for callers that haven't migrated
 * yet but new UI relies on the canonical shape.
 */
export interface RorosRoleContractDTO {
  id: string;
  orgId: string;
  actorId: string | null;
  name: string;
  status: string;
  version: number;
  /** GOV-06 component 1: prose description of why this role exists. */
  purposeScope: string;
  /** GOV-06 component 2: array of `{ metricId, metricName?, access }`. */
  outcomesOwnership: Array<{ metricId: string; metricName?: string; access: 'read' | 'write' }>;
  /** GOV-06 component 3: array of `{ routineId, routineName? }`. */
  routineOwnership: Array<{ routineId: string; routineName?: string }>;
  /** GOV-06 component 4: per-impact-level rights (root/trunk/branch/leaf). */
  decisionRights: { root?: string; trunk?: string; branch?: string; leaf?: string } & Record<string, string>;
  /** GOV-06 component 5: tool grants + budget envelope. */
  toolAccessPolicy: { grants?: Array<{ tool: string; accessLevel: string }>; budget_cap_usd?: number | null } | unknown[];
  /** GOV-06 component 6: QC duties shape. */
  qcDuties: Record<string, unknown>;
  /** GOV-06 component 7: interface relationships (reportingTo / collaboratesWith / escalatesTo). */
  interfaces: Record<string, unknown>;
  /** GOV-06 component 8: upgrade rules. */
  upgradeRules: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export const RorosRoleContractService = {
  list: (params?: Record<string, string>) =>
    fetchJson<RorosRoleContractDTO[]>(`/api/v1/role-contracts${qs(params)}`),
  listTemplates: () =>
    fetchJson<RorosRoleContractDTO[]>('/api/v1/role-contracts/templates'),
  create: (payload: Record<string, unknown>) =>
    mutate<RorosRoleContractDTO>('/api/v1/role-contracts', 'POST', payload),
  cloneFromTemplate: (templateId: string, payload: Record<string, unknown>) =>
    mutate<RorosRoleContractDTO>(`/api/v1/role-contracts/from-template/${templateId}`, 'POST', payload),
  get: (id: string) =>
    fetchJson<RorosRoleContractDTO>(`/api/v1/role-contracts/${id}`),
  update: (id: string, payload: Record<string, unknown>) =>
    mutate<RorosRoleContractDTO>(`/api/v1/role-contracts/${id}`, 'PUT', payload),
  activate: (id: string) =>
    mutate<RorosRoleContractDTO>(`/api/v1/role-contracts/${id}/activate`, 'POST'),
  archive: (id: string) =>
    mutate<RorosRoleContractDTO>(`/api/v1/role-contracts/${id}/archive`, 'POST'),
};

// ─── Budget Ledger (J7-W2 task 5) ──────────────────────────────────────────

export interface BudgetLedgerCallDTO {
  id: string;
  createdAt: string;
  purpose: string;
  model: string;
  totalCostUsd: number;
  status: string;
}

export interface BudgetLedgerOperatorDTO {
  actorId: string;
  actorName: string;
  contractId: string | null;
  contractName: string | null;
  capUsd: number | null;
  spendUsd: number;
  callCount: number;
  isLockedOut: boolean;
}

export interface BudgetLedgerDayPointDTO {
  day: string;
  spendUsd: number;
  callCount: number;
}

export interface BudgetLedgerDTO {
  currentMonth: {
    spendUsd: number;
    capUsd: number | null;
    isLockedOut: boolean;
    calls: BudgetLedgerCallDTO[];
    operators: BudgetLedgerOperatorDTO[];
  };
  lastNDays: BudgetLedgerDayPointDTO[];
}

export const RorosBudgetLedgerService = {
  get: (params?: { actorId?: string; days?: number }) => {
    const qsParams: Record<string, string> = {};
    if (params?.actorId) qsParams.actorId = params.actorId;
    if (params?.days) qsParams.days = String(params.days);
    return fetchJson<BudgetLedgerDTO>(`/api/v1/governance/budget-ledger${qs(qsParams)}`);
  },
};
